import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Image as ImageIcon, BookOpen, Share2, Mail, LayoutTemplate, Video, Type, FileText,
  Copy, Download, ExternalLink, FolderOpen,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICON = {
  "Logos": ImageIcon,
  "Brand Guidelines": BookOpen,
  "Social Media Graphics": Share2,
  "Email Templates": Mail,
  "Website Banners": LayoutTemplate,
  "Videos": Video,
  "Swipe Copy": Type,
  "PDFs": FileText,
};

const previewUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/marketing/resources/${id}/preview`;
const downloadUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/marketing/resources/${id}/download`;

const ResourceCard = ({ r }) => {
  const Icon = CATEGORY_ICON[r.category] || FolderOpen;
  const track = () => { api.post(`/marketing/resources/${r.id}/track`).catch(() => {}); };
  const copyText = () => { navigator.clipboard.writeText(r.body_text || ""); track(); toast.success("Copied to clipboard"); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white border border-[var(--ir-border)] rounded-lg overflow-hidden soft-shadow flex flex-col"
      data-testid={`resource-card-${r.id}`}
    >
      <div className="relative h-40 bg-[var(--ir-surface2)] flex items-center justify-center overflow-hidden">
        {r.has_preview ? (
          <img src={previewUrl(r.id)} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Icon className="w-12 h-12 text-[var(--ir-muted)]" />
        )}
        <Badge className="absolute top-2 left-2 rounded-none border bg-white/90 text-[var(--ir-blue)] border-[var(--ir-border)] text-[10px]">{r.category}</Badge>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h4 className="text-sm font-semibold text-[var(--ir-text)]">{r.title}</h4>
        {r.description && <p className="text-[var(--ir-muted)] text-xs mt-1 leading-relaxed">{r.description}</p>}

        {r.kind === "text" && (
          <div className="mt-3 bg-[var(--ir-surface2)] border border-[var(--ir-border)] rounded p-3">
            <p className="text-[var(--ir-text)] text-xs whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto" data-testid={`resource-text-${r.id}`}>{r.body_text}</p>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-[var(--ir-border)] flex items-center gap-2 mt-auto">
          {r.kind === "text" && (
            <Button onClick={copyText} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none h-9 flex-1" data-testid={`copy-swipe-btn-${r.id}`}>
              <Copy className="w-4 h-4 mr-2" />Copy text
            </Button>
          )}
          {r.kind === "link" && (
            <a href={r.external_url} onClick={track} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid={`open-link-${r.id}`}>
              <Button className="bg-[var(--ir-blue)] hover:opacity-90 text-white rounded-none h-9 w-full">
                <ExternalLink className="w-4 h-4 mr-2" />Open
              </Button>
            </a>
          )}
          {r.kind === "file" && (
            <a href={downloadUrl(r.id)} className="flex-1" data-testid={`download-btn-${r.id}`}>
              <Button className="bg-[var(--ir-blue)] hover:opacity-90 text-white rounded-none h-9 w-full">
                <Download className="w-4 h-4 mr-2" />Download
              </Button>
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const MarketingResources = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { api.get("/marketing/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const q = active === "All" ? "" : `?category=${encodeURIComponent(active)}`;
    api.get(`/marketing/resources${q}`).then((r) => { setItems(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, [active]);

  const chips = ["All", ...cats];

  return (
    <div data-testid="marketing-resources">
      <div className="flex items-center gap-2 mb-5">
        <FolderOpen className="w-6 h-6 text-[var(--ir-orange)]" />
        <h2 className="font-display text-3xl text-[var(--ir-text)]">Marketing Resources</h2>
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="marketing-category-filters">
        {chips.map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            className={`px-3 py-1.5 text-xs font-medium border rounded-none transition-colors ${active === c ? "bg-[var(--ir-orange)] text-white border-[var(--ir-orange)]" : "bg-white text-[var(--ir-text)] border-[var(--ir-border)] hover:bg-[var(--ir-surface2)]"}`}
            data-testid={`category-chip-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {loaded && items.length === 0 ? (
        <div className="bg-white border border-[var(--ir-border)] rounded-lg p-10 text-center text-[var(--ir-muted)] text-sm" data-testid="marketing-empty">
          No marketing materials in this category yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marketing-grid">
          {items.map((r) => <ResourceCard key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
};
